import React from 'react';
import { AlertTriangle, Clock, User } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';

interface ConflictResolutionDialogProps {
  open: boolean;
  currentSettings: OrganisationTimesheetSettings;
  serverSettings: OrganisationTimesheetSettings;
  onResolve: (action: 'discard' | 'override') => void;
  onCancel: () => void;
}

export const ConflictResolutionDialog: React.FC<ConflictResolutionDialogProps> = ({
  open,
  currentSettings,
  serverSettings,
  onResolve,
  onCancel
}) => {
  // Helper to find differences between settings
  const findDifferences = () => {
    const diffs: Array<{ field: string; yourValue: any; theirValue: any }> = [];
    
    // Compare deadline settings
    if (currentSettings.deadline && serverSettings.deadline) {
      const current = currentSettings.deadline;
      const server = serverSettings.deadline;
      
      if (current.deadlineDay !== server.deadlineDay) {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        diffs.push({
          field: 'Weekly Deadline Day',
          yourValue: dayNames[current.deadlineDay] || current.deadlineDay,
          theirValue: dayNames[server.deadlineDay] || server.deadlineDay
        });
      }
      
      if (current.deadlineTime !== server.deadlineTime) {
        diffs.push({
          field: 'Weekly Deadline Time',
          yourValue: current.deadlineTime,
          theirValue: server.deadlineTime
        });
      }
      
      if (current.gracePeriodDays !== server.gracePeriodDays) {
        diffs.push({
          field: 'Grace Period',
          yourValue: `${current.gracePeriodDays} days`,
          theirValue: `${server.gracePeriodDays} days`
        });
      }
    }
    
    // Compare utilisation settings
    if (currentSettings.utilisation && serverSettings.utilisation) {
      const current = currentSettings.utilisation;
      const server = serverSettings.utilisation;
      
      if (current.targetUtilisationRate !== server.targetUtilisationRate) {
        diffs.push({
          field: 'Utilisation Target',
          yourValue: `${Math.round(current.targetUtilisationRate * 100)}%`,
          theirValue: `${Math.round(server.targetUtilisationRate * 100)}%`
        });
      }
      
      if (current.calculationMethod !== server.calculationMethod) {
        diffs.push({
          field: 'Calculation Method',
          yourValue: current.calculationMethod,
          theirValue: server.calculationMethod
        });
      }
    }
    
    // Compare week start day
    if (currentSettings.weekStartDay !== serverSettings.weekStartDay) {
      diffs.push({
        field: 'Week Start Day',
        yourValue: currentSettings.weekStartDay,
        theirValue: serverSettings.weekStartDay
      });
    }
    
    // Compare boolean flags
    if (currentSettings.showRole !== serverSettings.showRole) {
      diffs.push({
        field: 'Show Role',
        yourValue: currentSettings.showRole ? 'Enabled' : 'Disabled',
        theirValue: serverSettings.showRole ? 'Enabled' : 'Disabled'
      });
    }
    
    if (currentSettings.requireRole !== serverSettings.requireRole) {
      diffs.push({
        field: 'Require Role',
        yourValue: currentSettings.requireRole ? 'Required' : 'Optional',
        theirValue: serverSettings.requireRole ? 'Required' : 'Optional'
      });
    }
    
    return diffs;
  };
  
  const differences = findDifferences();
  
  return (
    <AlertDialog open={open} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <AlertDialogTitle>Settings Conflict Detected</AlertDialogTitle>
              <AlertDialogDescription>
                Another administrator has modified these settings while you were editing.
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        
        <div className="flex-1 overflow-y-auto min-h-0">
        <div className="space-y-4 my-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Their changes were saved more recently than yours</span>
          </div>
          
          {differences.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Conflicting Changes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {differences.map((diff, index) => (
                    <div key={index} className="grid grid-cols-3 gap-4 py-3 border-b last:border-b-0">
                      <div className="font-medium text-sm">{diff.field}</div>
                      <div className="text-sm">
                        <Badge variant="outline" className="bg-blue-50">Your value</Badge>
                        <div className="mt-1 text-muted-foreground">{diff.yourValue}</div>
                      </div>
                      <div className="text-sm">
                        <Badge variant="outline" className="bg-amber-50">Their value</Badge>
                        <div className="mt-1 text-muted-foreground">{diff.theirValue}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-6 text-center text-sm text-muted-foreground">
                Unable to determine specific differences. The settings have been modified.
              </CardContent>
            </Card>
          )}
          
          <div className="bg-muted p-4 rounded-lg space-y-2">
            <div className="font-medium text-sm">What would you like to do?</div>
            <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
              <li><strong>Discard My Changes:</strong> Keep the other administrator's settings and lose your changes</li>
              <li><strong>Override Their Changes:</strong> Save your settings and overwrite their changes</li>
            </ul>
          </div>
        </div>
        </div>
        
        <AlertDialogFooter className="shrink-0 border-t pt-4">
          <AlertDialogCancel onClick={() => onResolve('discard')}>
            Discard My Changes
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={() => onResolve('override')}
            className="bg-amber-500 hover:bg-amber-600"
          >
            Override Their Changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
